import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

export function EmergencyDisclaimer() {
  return (
    <div className="legal-page" data-testid="emergency-disclaimer-page">
      <div className="legal-container">
        <Link to="/" className="legal-back-link" data-testid="back-link">
          <ArrowLeft size={18} />
          <span>Back</span>
        </Link>

        <div className="legal-header">
          <div className="legal-icon legal-icon-warning">
            <AlertTriangle size={24} />
          </div>
          <h1>Emergency Disclaimer</h1>
          <p className="legal-updated">Last updated: January 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Purpose of Emergency Mode</h2>
            <p>
              TradeLife includes an Emergency Mode feature designed to help you escalate urgent situations 
              to relevant parties. This feature facilitates communication and escalation processes within 
              the platform.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. What Emergency Mode Does</h2>
            <p>Emergency Mode provides tools to:</p>
            <ul>
              <li>Notify designated contacts about urgent situations</li>
              <li>Escalate priority of jobs or requests</li>
              <li>Log and track emergency communications</li>
              <li>Facilitate faster coordination between parties</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. What Emergency Mode Does Not Guarantee</h2>
            <p>
              <strong>Emergency Mode does not:</strong>
            </p>
            <ul>
              <li>Guarantee attendance by any tradesperson or contractor</li>
              <li>Guarantee response times from any party</li>
              <li>Guarantee resolution of any emergency situation</li>
              <li>Guarantee availability of services</li>
              <li>Replace emergency services (999, fire, police, ambulance)</li>
              <li>Provide emergency response services of any kind</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. User Responsibilities</h2>
            <p>When using Emergency Mode, you remain responsible for:</p>
            <ul>
              <li><strong>Contacting appropriate emergency services</strong> (999) for genuine emergencies involving risk to life or property</li>
              <li><strong>Assessing the situation</strong> and taking appropriate immediate action</li>
              <li><strong>Having backup contacts and plans</strong> for urgent situations</li>
              <li><strong>Not relying solely on this feature</strong> for emergency response</li>
              <li><strong>Ensuring contacts listed</strong> are willing and able to respond</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Escalation Limitations</h2>
            <p>
              The escalation features within TradeLife:
            </p>
            <ul>
              <li>Send notifications only; they cannot force anyone to respond or attend</li>
              <li>Depend on the availability and responsiveness of the notified parties</li>
              <li>May be affected by technical issues, network availability, or device settings</li>
              <li>Do not create any obligation for TradeLife to provide emergency services</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. No Guarantees on Outcomes</h2>
            <p>
              TradeLife is a software platform that facilitates communication. We cannot and do not guarantee:
            </p>
            <ul>
              <li>That any notification will be received, read, or acted upon</li>
              <li>That any tradesperson or contractor will respond or attend</li>
              <li>The quality, timeliness, or outcome of any work performed</li>
              <li>That any emergency situation will be resolved</li>
              <li>Any particular response time or service level</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Appropriate Use</h2>
            <p>Emergency Mode is intended for:</p>
            <ul>
              <li>Urgent business situations requiring expedited coordination</li>
              <li>Time-sensitive job escalations</li>
              <li>Situations where rapid communication is beneficial</li>
            </ul>
            <p>
              Emergency Mode is <strong>not</strong> intended as a replacement for:
            </p>
            <ul>
              <li>Emergency services (fire, police, ambulance)</li>
              <li>Medical emergencies</li>
              <li>Situations involving immediate risk to life</li>
              <li>Dangerous situations requiring immediate professional intervention</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Liability</h2>
            <p>
              TradeLife provides Emergency Mode as a communication facilitation tool only. We are not liable for:
            </p>
            <ul>
              <li>Failure of any party to respond to notifications</li>
              <li>Delays in communication or response</li>
              <li>Actions or inactions of any tradespeople, contractors, or other parties</li>
              <li>Any damages, losses, or injuries arising from emergency situations</li>
              <li>Technical failures affecting notification delivery</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. For Genuine Emergencies</h2>
            <div className="legal-callout">
              <p>
                <strong>If you are experiencing a genuine emergency involving risk to life, health, or property, 
                please contact the appropriate emergency services immediately:</strong>
              </p>
              <ul>
                <li><strong>Emergency Services:</strong> 999</li>
                <li><strong>Non-emergency Police:</strong> 101</li>
                <li><strong>NHS Non-emergency:</strong> 111</li>
                <li><strong>Gas Emergency:</strong> 0800 111 999</li>
              </ul>
              <p>
                Do not rely on TradeLife or any software platform as your primary method of 
                addressing genuine emergencies.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2>10. Contact Us</h2>
            <p>If you have questions about Emergency Mode, contact us at:</p>
            <p className="legal-contact">
              <strong>Email:</strong> support@tradelife.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default EmergencyDisclaimer;
